import React, { useState } from "react"
import gql from "graphql-tag"
import { useMutation, useQuery } from "react-apollo"
import PropTypes from "prop-types"
import { MdEdit } from "react-icons/md"
import * as Yup from "yup"
import {
  Button,
  Text,
  useShowSuccessToast,
  RadioButtonConnectedField,
  InputConnectedField,
  ConcealedValue,
} from "gatsby-interface"
import {
  SettingsCardContent,
  SettingsCardEditButton,
} from "@modules/site/settings"
import { MdArrowForward } from "react-icons/md"
import { hideOnMobileCss } from "@modules/ui/utils/responsive"
import { visitorAccess as text } from "@modules/locales/default.json"
import { useTracker } from "@modules/analytics"
import { SiteSettingsSection } from "../constants"
import SiteSettingsSectionCard from "./SiteSettingsSectionCard"
import { PreviewProtectionType } from "@modules/graphql/types"
import { useTriggerErrorAlert } from "@modules/ui/components/ErrorAlert"
import { visitorAccessOptions } from "../constants"
import { updateVisitorAccessCache } from "./VisitorAccess.helpers"
import Form from "../../../form/components/Form"
import CopyUrl from "../../../ui/components/CopyUrl"
import { FadeOnMount } from "@modules/animation"

const SET_PREVIEW_PROTECTION = gql`
  mutation setSitePreviewProtection(
    $id: UUID!
    $type: PreviewProtectionType!
    $password: String
  ) {
    setSitePreviewProtection(id: $id, type: $type, password: $password) {
      success
      message
    }
  }
`

const PREVIEW_AUTH_TOKEN = gql`
  query sitePreviewAuthToken($siteId: UUID!) {
    sitePreviewAuthToken(siteId: $siteId)
  }
`

const rowCss = theme => ({
  display: `grid`,

  [theme.mediaQueries.tablet]: {
    gridTemplateColumns: `20% 80%`,
  },
})

const labelCss = theme => ({
  color: theme.colors.grey[50],
  fontSize: theme.fontSizes[1],
  fontWeight: "normal",
})

const valueCss = theme => ({
  color: theme.colors.black,
  fontSize: theme.fontSizes[1],
  fontWeight: "normal",
})

const webhookSectionCss = theme => ({
  marginTop: theme.space[5],
})

const webhookTextCss = theme => ({
  marginTop: 0,
  marginBottom: theme.space[3],
  color: theme.colors.grey[50],
  fontSize: theme.fontSizes[1],
})

const optionLabelCss = theme => ({
  display: `flex`,
  flexDirection: `column`,

  [theme.mediaQueries.tablet]: {
    flexDirection: `row`,
    alignItems: `center`,
  },
})

const optionNameCss = theme => ({
  [theme.mediaQueries.tablet]: {
    marginRight: theme.space[4],
  },
})

const labelDescriptionCss = theme => ({
  color: theme.colors.grey[50],
  fontSize: theme.fontSizes[1],
})

function PreviewAuthenticationSection({ siteId }) {
  const { data } = useQuery(PREVIEW_AUTH_TOKEN, {
    variables: {
      siteId,
    },
  })
  const token = data && data.sitePreviewAuthToken

  return (
    <div css={webhookSectionCss}>
      <Text css={webhookTextCss}>
        Your Preview instance is not accessible to the public. If you need to
        bypass the visitor access check, please set the HTTP header below before
        making requests to your Preview instance URL
      </Text>

      {token && <CopyUrl url={token} urlDisplay={`Authorization: ${token}`} />}
    </div>
  )
}

function VisitorAccess({ siteId, sitePreviewProtection, sitePreviewPassword }) {
  const { trackButtonClicked } = useTracker()
  const [formVisible, setFormVisible] = useState(false)

  const currentOption = visitorAccessOptions.find(
    option => option.value === sitePreviewProtection
  )

  return (
    <SiteSettingsSectionCard
      id={SiteSettingsSection.VisitorAccess}
      renderTitle={defaultTitle =>
        !formVisible ? defaultTitle : text.editVisitorAccess
      }
    >
      <SettingsCardEditButton
        onClick={() => {
          trackButtonClicked(text.editAccess, {
            uiSource: `Site Settings`,
          })
          return setFormVisible(!formVisible)
        }}
      >
        <span aria-hidden="true" css={hideOnMobileCss}>
          {text.editAccess}
        </span>

        <MdEdit aria-label={text.editAccess} />
      </SettingsCardEditButton>
      <SettingsCardContent>
        {formVisible ? (
          <VisitorAccessForm
            siteId={siteId}
            sitePreviewProtection={sitePreviewProtection}
            sitePreviewPassword={sitePreviewPassword}
            onSuccess={() => setFormVisible(false)}
            onCancel={() => setFormVisible(false)}
          />
        ) : (
          <React.Fragment>
            <div css={rowCss}>
              <div css={labelCss}>{currentOption.label}</div>

              <div css={valueCss}>
                {currentOption.description}

                {sitePreviewProtection === PreviewProtectionType.Password && (
                  <div css={{ display: `inline-block` }}>
                    <ConcealedValue
                      value={sitePreviewPassword}
                      ariaLabel="Password"
                    />
                  </div>
                )}
              </div>
            </div>
            {sitePreviewProtection !== PreviewProtectionType.Public && (
              <PreviewAuthenticationSection siteId={siteId} origin={origin} />
            )}
          </React.Fragment>
        )}
      </SettingsCardContent>
    </SiteSettingsSectionCard>
  )
}

VisitorAccess.propTypes = {
  siteId: PropTypes.string.isRequired,
  sitePreviewProtection: PropTypes.string.isRequired,
  sitePreviewPassword: PropTypes.string,
}

export default VisitorAccess

const validationSchema = Yup.object().shape({
  previewProtect: Yup.string().required(`Visitor Access Type is required`),
  previewPass: Yup.string().test(`previewPass`, text.cantBeEmpty, function(
    val
  ) {
    const previewProtect = this.parent.previewProtect

    if (previewProtect === `PASSWORD` && !val) {
      return false
    }
    return true
  }),
})

function VisitorAccessForm({
  siteId,
  sitePreviewProtection,
  sitePreviewPassword,
  onSuccess,
  onCancel,
}) {
  const [setPreviewProtection, { loading }] = useMutation(
    SET_PREVIEW_PROTECTION
  )
  const { trackButtonClicked } = useTracker()
  const showSuccessToast = useShowSuccessToast()
  const [setError, errorAlert] = useTriggerErrorAlert()

  return (
    <Form
      validationSchema={validationSchema}
      onSubmit={values => {
        setError(null)
        trackButtonClicked(`save visitor access`, {
          uiSource: `Site Settings`,
        })
        return setPreviewProtection({
          variables: {
            id: siteId,
            type: values.previewProtect,
            password: values.previewPass ? values.previewPass : undefined,
          },
          update: updateVisitorAccessCache(siteId, values),
        })
          .then(() =>
            showSuccessToast(
              <React.Fragment>
                <strong>{text.visitorAccess}</strong> {text.updated}
              </React.Fragment>
            )
          )
          .then(() => onSuccess())
          .catch(err => setError(err))
      }}
      initialValues={{
        previewPass: sitePreviewPassword ? sitePreviewPassword : ``,
        previewProtect: sitePreviewProtection || PreviewProtectionType.Public,
      }}
    >
      {({ submitForm, values }) => (
        <Form.FormElement applySpacing>
          <RadioButtonConnectedField
            name="previewProtect"
            label="Visitor Access"
            css={theme => ({
              marginBottom: theme.space[6],
            })}
            options={visitorAccessOptions.map(
              ({ label, value, description }) => {
                return {
                  value,
                  label: (
                    <div css={optionLabelCss}>
                      <span css={optionNameCss}>{label}:</span>
                      <small css={labelDescriptionCss}>{description}</small>
                    </div>
                  ),
                }
              }
            )}
          />
          {values.previewProtect === PreviewProtectionType.Password && (
            <FadeOnMount>
              <InputConnectedField
                name="previewPass"
                label="Preview Password"
                type={"password"}
                data-cy="visitor-access-password-input"
              />
            </FadeOnMount>
          )}
          {errorAlert}
          <Form.Actions>
            <Button
              variant="SECONDARY"
              tone="NEUTRAL"
              type={`reset`}
              onClick={() => {
                trackButtonClicked(`cancel visitor access`, {
                  uiSource: `Site Settings`,
                })
                return onCancel()
              }}
            >
              {text.cancel}
            </Button>
            <Button
              type="button"
              onClick={submitForm}
              loading={loading}
              rightIcon={<MdArrowForward />}
            >
              {text.save}
            </Button>
          </Form.Actions>
        </Form.FormElement>
      )}
    </Form>
  )
}

VisitorAccessForm.propTypes = {
  siteId: PropTypes.string.isRequired,
  sitePreviewProtection: PropTypes.string.isRequired,
  sitePreviewPassword: PropTypes.string,
  onSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
}
